import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const {
  FiVideo, FiRadio, FiKey, FiEye, FiEyeOff, FiSave,
  FiCheck, FiStar, FiGlobe, FiLink, FiCopy, FiInfo,
  FiToggleLeft, FiSliders, FiChevronDown, FiChevronUp,
} = FiIcons;

// ── Platform data ─────────────────────────────────────────────────────────────
const WEBINAR_PLATFORMS = [
  { id: 'zoom',   label: 'Zoom',            emoji: '🔵', color: '#2D8CFF', bg: 'bg-[#2D8CFF]/10', text: 'text-[#2D8CFF]', border: 'border-[#2D8CFF]/30', fields: [{ key: 'apiKey', label: 'API Key' }, { key: 'accountId', label: 'Account ID' }, { key: 'clientId', label: 'Client ID' }, { key: 'clientSecret', label: 'Client Secret' }] },
  { id: 'gmeet',  label: 'Google Meet',     emoji: '🟢', color: '#34A853', bg: 'bg-[#34A853]/10', text: 'text-[#34A853]', border: 'border-[#34A853]/30', fields: [{ key: 'clientId', label: 'OAuth Client ID' }, { key: 'clientSecret', label: 'Client Secret' }, { key: 'apiKey', label: 'API Key' }] },
  { id: 'teams',  label: 'Microsoft Teams', emoji: '🟣', color: '#6264A7', bg: 'bg-[#6264A7]/10', text: 'text-[#6264A7]', border: 'border-[#6264A7]/30', fields: [{ key: 'tenantId', label: 'Tenant ID' }, { key: 'clientId', label: 'Client ID' }, { key: 'clientSecret', label: 'Client Secret' }] },
  { id: 'jitsi',  label: 'Jitsi Meet',      emoji: '🟠', color: '#EA7D24', bg: 'bg-[#EA7D24]/10', text: 'text-[#EA7D24]', border: 'border-[#EA7D24]/30', fields: [{ key: 'appId', label: 'App ID' }, { key: 'serverUrl', label: 'Server URL' }, { key: 'jwtSecret', label: 'JWT Secret' }] },
  { id: 'native', label: 'Native Stream',   emoji: '🔴', color: '#E53E3E', bg: 'bg-[#E53E3E]/10', text: 'text-[#E53E3E]', border: 'border-[#E53E3E]/30', fields: [{ key: 'rtmpServer', label: 'RTMP Server' }, { key: 'hlsBase', label: 'HLS Base URL' }, { key: 'streamToken', label: 'Stream Token' }] },
];

const STREAMING_PLATFORMS = [
  { id: 'youtube',   label: 'YouTube Live',    emoji: '▶️', color: '#FF0000', bg: 'bg-[#FF0000]/10', text: 'text-[#FF0000]', border: 'border-[#FF0000]/30', rtmpBase: 'rtmp://a.rtmp.youtube.com/live2/', fields: [{ key: 'channelId', label: 'Channel ID' }, { key: 'streamKey', label: 'Stream Key' }, { key: 'apiKey', label: 'API Key' }] },
  { id: 'facebook',  label: 'Facebook Live',   emoji: '📘', color: '#1877F2', bg: 'bg-[#1877F2]/10', text: 'text-[#1877F2]', border: 'border-[#1877F2]/30', rtmpBase: 'rtmps://live-api-s.facebook.com:443/rtmp/', fields: [{ key: 'pageId', label: 'Page ID' }, { key: 'streamKey', label: 'Stream Key' }, { key: 'accessToken', label: 'Page Access Token' }] },
  { id: 'instagram', label: 'Instagram Live',  emoji: '📸', color: '#E1306C', bg: 'bg-[#E1306C]/10', text: 'text-[#E1306C]', border: 'border-[#E1306C]/30', rtmpBase: 'rtmps://edgetee-upload.facebook.com:443/rtmp/', fields: [{ key: 'igUserId', label: 'Instagram User ID' }, { key: 'streamKey', label: 'Stream Key' }, { key: 'userAccessToken', label: 'User Access Token' }] },
  { id: 'twitch',    label: 'Twitch',          emoji: '🟣', color: '#9146FF', bg: 'bg-[#9146FF]/10', text: 'text-[#9146FF]', border: 'border-[#9146FF]/30', rtmpBase: 'rtmp://live.twitch.tv/app/', fields: [{ key: 'broadcasterId', label: 'Broadcaster ID' }, { key: 'streamKey', label: 'Stream Key' }, { key: 'clientId', label: 'Client ID' }] },
];

// ── Helpers ───────────────────────────────────────────────────────────────────
const loadSettings = (memorialId) => {
  try { return JSON.parse(localStorage.getItem(`em_memorial_platform_${memorialId}`) || '{}'); }
  catch { return {}; }
};

const saveSettings = (memorialId, data) => {
  localStorage.setItem(`em_memorial_platform_${memorialId}`, JSON.stringify(data));
};

// ── Shared primitives ─────────────────────────────────────────────────────────
const SecretField = ({ label, value, onChange }) => {
  const [visible, setVisible] = useState(false);
  const isSecret = /secret|token|key/i.test(label);
  return (
    <div className="space-y-1">
      <label className="text-[9px] font-black uppercase tracking-widest text-accent-navy/70 block">{label}</label>
      <div className="relative">
        <input
          type={isSecret && !visible ? 'password' : 'text'}
          value={value}
          onChange={e => onChange(e.target.value)}
          placeholder={isSecret ? '••••••••••••' : 'Enter value…'}
          className="w-full pl-3 pr-10 py-2.5 bg-brand-light border-2 border-brand-dark rounded-xl outline-none focus:border-accent-gold transition-all text-[11px] font-mono"
        />
        {isSecret && (
          <button type="button" onClick={() => setVisible(v => !v)}
            className="absolute right-2.5 top-1/2 -translate-y-1/2 text-text-muted hover:text-accent-navy transition-colors">
            <SafeIcon icon={visible ? FiEyeOff : FiEye} className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    </div>
  );
};

const Toggle = ({ value, onChange, label, description }) => (
  <div className="flex items-center justify-between gap-4 py-3">
    <div>
      <p className="text-xs font-bold text-accent-navy">{label}</p>
      {description && <p className="text-[10px] text-text-muted leading-relaxed">{description}</p>}
    </div>
    <button
      type="button"
      onClick={() => onChange(!value)}
      className={`w-11 h-6 rounded-full relative flex-shrink-0 transition-colors duration-300 ${value ? 'bg-accent-gold' : 'bg-brand-dark'}`}
    >
      <motion.div
        animate={{ x: value ? 22 : 2 }}
        transition={{ type: 'spring', stiffness: 500, damping: 30 }}
        className="absolute top-1 w-4 h-4 bg-white rounded-full shadow-sm"
      />
    </button>
  </div>
);

const InfoBanner = ({ emoji, children, color = 'blue' }) => {
  const cls = {
    blue: 'bg-blue-50 border-blue-200 text-blue-700',
    amber: 'bg-amber-50 border-amber-200 text-amber-700',
    green: 'bg-green-50 border-green-200 text-green-700',
  }[color];
  return (
    <div className={`flex items-start gap-3 border rounded-2xl p-4 ${cls}`}>
      <span className="text-sm flex-shrink-0">{emoji}</span>
      <p className="text-[10px] leading-relaxed">{children}</p>
    </div>
  );
};

const SectionHeader = ({ icon, title, subtitle }) => (
  <div className="flex items-center gap-3 pb-4 border-b border-brand-dark">
    <div className="p-2.5 bg-brand-light rounded-xl">
      <SafeIcon icon={icon} className="w-4 h-4 text-accent-navy" />
    </div>
    <div>
      <h4 className="font-serif text-accent-navy">{title}</h4>
      <p className="text-[10px] text-text-muted uppercase tracking-widest font-black">{subtitle}</p>
    </div>
  </div>
);

// ── Platform row (collapsible) ────────────────────────────────────────────────
const PlatformRow = ({ platform, isDefault, keys, onKeyChange, onSetDefault, isExpanded, onToggleExpand }) => (
  <div className={`rounded-[20px] border-2 transition-all overflow-hidden ${isDefault ? `${platform.border} bg-white shadow-md` : 'border-brand-dark bg-brand-light'}`}>
    <button
      type="button"
      onClick={onToggleExpand}
      className="w-full flex items-center gap-4 p-4 text-left"
    >
      <div className={`w-9 h-9 ${platform.bg} rounded-xl flex items-center justify-center text-lg flex-shrink-0`}>
        {platform.emoji}
      </div>
      <div className="flex-grow min-w-0">
        <p className={`text-xs font-black ${isDefault ? platform.text : 'text-accent-navy'}`}>{platform.label}</p>
        <p className="text-[9px] text-text-muted">
          {isDefault ? '⭐ Selected as default' : 'Click to configure'}
        </p>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        {!isDefault && (
          <button
            type="button"
            onClick={e => { e.stopPropagation(); onSetDefault(); }}
            className="text-[8px] font-black uppercase tracking-widest text-text-muted hover:text-accent-gold transition-colors border border-brand-dark rounded-lg px-2 py-1 flex items-center gap-1 bg-white"
          >
            <SafeIcon icon={FiStar} className="w-2.5 h-2.5" /> Default
          </button>
        )}
        <SafeIcon icon={isExpanded ? FiChevronUp : FiChevronDown} className="w-4 h-4 text-text-muted" />
      </div>
    </button>

    <AnimatePresence initial={false}>
      {isExpanded && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="overflow-hidden"
        >
          <div className="px-4 pb-5 pt-1 border-t border-brand-dark/30 space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {platform.fields.map(f => (
                <SecretField
                  key={f.key}
                  label={f.label}
                  value={keys[f.key] || ''}
                  onChange={val => onKeyChange(f.key, val)}
                />
              ))}
            </div>
            {platform.rtmpBase && (
              <div className="bg-accent-navy/5 rounded-xl p-3 flex items-center justify-between gap-3">
                <div>
                  <p className="text-[9px] font-black uppercase tracking-widest text-text-muted mb-0.5">RTMP Server</p>
                  <code className="text-[10px] text-accent-navy">{platform.rtmpBase}{keys.streamKey || '<stream-key>'}</code>
                </div>
                <button
                  type="button"
                  onClick={() => navigator.clipboard.writeText(`${platform.rtmpBase}${keys.streamKey || ''}`).catch(() => {})}
                  className="flex-shrink-0 px-3 py-1.5 rounded-lg border border-brand-dark bg-white text-[9px] font-black uppercase tracking-widest text-text-muted hover:border-accent-gold transition-colors flex items-center gap-1"
                >
                  <SafeIcon icon={FiCopy} className="w-3 h-3" /> Copy
                </button>
              </div>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  </div>
);

// ── MAIN COMPONENT ────────────────────────────────────────────────────────────
const MemorialPlatformSettings = ({ memorial }) => {
  const memId = memorial?.id || 'default';
  const stored = loadSettings(memId);

  // Override toggles
  const [overrideWebinar, setOverrideWebinar] = useState(stored.overrideWebinar ?? false);
  const [overrideStreaming, setOverrideStreaming] = useState(stored.overrideStreaming ?? false);

  // Per-memorial defaults
  const [webinarDefault, setWebinarDefault] = useState(stored.webinarDefault || 'zoom');
  const [streamingDefault, setStreamingDefault] = useState(stored.streamingDefault || 'youtube');

  // Per-platform keys: { zoom: { apiKey: '...' }, ... }
  const [webinarKeys, setWebinarKeys] = useState(stored.webinarKeys || {});
  const [streamingKeys, setStreamingKeys] = useState(stored.streamingKeys || {});

  // Expanded accordion rows
  const [expandedWebinar, setExpandedWebinar] = useState(null);
  const [expandedStreaming, setExpandedStreaming] = useState(null);

  const [saved, setSaved] = useState(false);

  const setWebinarKey = (platformId, fieldKey, val) =>
    setWebinarKeys(prev => ({ ...prev, [platformId]: { ...(prev[platformId] || {}), [fieldKey]: val } }));

  const setStreamingKey = (platformId, fieldKey, val) =>
    setStreamingKeys(prev => ({ ...prev, [platformId]: { ...(prev[platformId] || {}), [fieldKey]: val } }));

  const handleSave = () => {
    saveSettings(memId, { overrideWebinar, overrideStreaming, webinarDefault, streamingDefault, webinarKeys, streamingKeys });
    window.dispatchEvent(new CustomEvent('memorial_platform_settings_updated', { detail: { memorialId: memId, webinarDefault: overrideWebinar ? webinarDefault : null, streamingDefault: overrideStreaming ? streamingDefault : null } }));
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  return (
    <div className="space-y-8 pb-4">

      {/* Header */}
      <div className="bg-accent-navy text-white rounded-[32px] p-7 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10 pointer-events-none">
          <div className="absolute top-0 right-0 w-72 h-72 bg-accent-gold rounded-full -mr-36 -mt-36 blur-3xl" />
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-blue-400 rounded-full -ml-24 -mb-24 blur-3xl" />
        </div>
        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-2">
            <SafeIcon icon={FiSliders} className="w-5 h-5 text-accent-gold" />
            <h3 className="text-xl font-serif">Platform Preferences</h3>
          </div>
          <p className="text-white/60 text-sm font-light leading-relaxed max-w-2xl">
            Override the site-wide defaults by configuring dedicated webinar and streaming accounts specifically for <strong className="text-white/90">{memorial?.name || 'this memorial'}</strong>. Leave overrides off to inherit the global platform settings.
          </p>
        </div>
        <div className="relative z-10 mt-5 pt-5 border-t border-white/10 grid grid-cols-2 gap-4">
          <div className="flex items-center gap-3">
            <div className={`w-2.5 h-2.5 rounded-full ${overrideWebinar ? 'bg-green-400 animate-pulse' : 'bg-white/20'}`} />
            <p className="text-[10px] font-black uppercase tracking-widest text-white/60">
              Webinar Override: <span className={overrideWebinar ? 'text-green-400' : 'text-white/30'}>{overrideWebinar ? 'Active' : 'Inheriting Global'}</span>
            </p>
          </div>
          <div className="flex items-center gap-3">
            <div className={`w-2.5 h-2.5 rounded-full ${overrideStreaming ? 'bg-green-400 animate-pulse' : 'bg-white/20'}`} />
            <p className="text-[10px] font-black uppercase tracking-widest text-white/60">
              Streaming Override: <span className={overrideStreaming ? 'text-green-400' : 'text-white/30'}>{overrideStreaming ? 'Active' : 'Inheriting Global'}</span>
            </p>
          </div>
        </div>
      </div>

      {/* ── WEBINAR / MEETING SECTION ── */}
      <div className="bg-white rounded-[32px] border border-brand-dark p-7 space-y-6">
        <SectionHeader icon={FiVideo} title="Webinar & Meeting Platform" subtitle="Per-memorial override" />

        <Toggle
          value={overrideWebinar}
          onChange={setOverrideWebinar}
          label="Override Global Webinar Settings"
          description="Use dedicated meeting credentials for this memorial instead of the platform-wide defaults."
        />

        <AnimatePresence>
          {overrideWebinar && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.25 }}
              className="overflow-hidden space-y-4"
            >
              <InfoBanner emoji="ℹ️" color="blue">
                Configure account credentials for the meeting platform you want to use for this memorial's events. Only the selected default platform will be used when scheduling.
              </InfoBanner>

              {/* Default selector chips */}
              <div>
                <p className="text-[10px] font-black uppercase tracking-widest text-accent-navy mb-3">Default Meeting Platform for This Memorial</p>
                <div className="flex flex-wrap gap-2">
                  {WEBINAR_PLATFORMS.map(p => (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => setWebinarDefault(p.id)}
                      className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border-2 text-[10px] font-black uppercase tracking-widest transition-all ${webinarDefault === p.id ? `${p.border} bg-white shadow-sm ${p.text}` : 'border-brand-dark bg-brand-light text-text-muted hover:border-accent-gold/40'}`}
                    >
                      <span>{p.emoji}</span> {p.label}
                      {webinarDefault === p.id && <SafeIcon icon={FiCheck} className="w-3 h-3" />}
                    </button>
                  ))}
                </div>
              </div>

              {/* Platform rows */}
              <div className="space-y-3">
                {WEBINAR_PLATFORMS.map(p => (
                  <PlatformRow
                    key={p.id}
                    platform={p}
                    isDefault={webinarDefault === p.id}
                    keys={webinarKeys[p.id] || {}}
                    onKeyChange={(fk, val) => setWebinarKey(p.id, fk, val)}
                    onSetDefault={() => setWebinarDefault(p.id)}
                    isExpanded={expandedWebinar === p.id}
                    onToggleExpand={() => setExpandedWebinar(prev => prev === p.id ? null : p.id)}
                  />
                ))}
              </div>

              <InfoBanner emoji="🔐" color="amber">
                Credentials entered here are stored locally and scoped to this memorial only. They take precedence over any keys configured in the Site Administration Hub for this memorial's events.
              </InfoBanner>
            </motion.div>
          )}
        </AnimatePresence>

        {!overrideWebinar && (
          <div className="flex items-center gap-3 p-4 bg-brand-light rounded-2xl border border-brand-dark">
            <SafeIcon icon={FiGlobe} className="w-4 h-4 text-text-muted flex-shrink-0" />
            <p className="text-[11px] text-text-muted leading-relaxed">
              Using global default: <strong className="text-accent-navy">{WEBINAR_PLATFORMS.find(p => p.id === ((() => { try { return JSON.parse(localStorage.getItem('em_webinar_integrations') || '{}').defaultPlatform; } catch { return 'zoom'; } })()))?.label || 'Zoom'}</strong>. Enable the override above to configure a different platform for this memorial.
            </p>
          </div>
        )}
      </div>

      {/* ── STREAMING SECTION ── */}
      <div className="bg-white rounded-[32px] border border-brand-dark p-7 space-y-6">
        <SectionHeader icon={FiRadio} title="Live Streaming Platform" subtitle="Per-memorial override" />

        <Toggle
          value={overrideStreaming}
          onChange={setOverrideStreaming}
          label="Override Global Streaming Settings"
          description="Use a dedicated streaming account and stream key for this memorial's live broadcasts."
        />

        <AnimatePresence>
          {overrideStreaming && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.25 }}
              className="overflow-hidden space-y-4"
            >
              <InfoBanner emoji="📡" color="blue">
                Enter the stream key and account details for the platform you want to broadcast to from this memorial. The RTMP ingest URL will be auto-computed for your broadcast software.
              </InfoBanner>

              {/* Default selector chips */}
              <div>
                <p className="text-[10px] font-black uppercase tracking-widest text-accent-navy mb-3">Default Streaming Platform for This Memorial</p>
                <div className="flex flex-wrap gap-2">
                  {STREAMING_PLATFORMS.map(p => (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => setStreamingDefault(p.id)}
                      className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border-2 text-[10px] font-black uppercase tracking-widest transition-all ${streamingDefault === p.id ? `${p.border} bg-white shadow-sm ${p.text}` : 'border-brand-dark bg-brand-light text-text-muted hover:border-accent-gold/40'}`}
                    >
                      <span>{p.emoji}</span> {p.label}
                      {streamingDefault === p.id && <SafeIcon icon={FiCheck} className="w-3 h-3" />}
                    </button>
                  ))}
                </div>
              </div>

              {/* Platform rows */}
              <div className="space-y-3">
                {STREAMING_PLATFORMS.map(p => (
                  <PlatformRow
                    key={p.id}
                    platform={p}
                    isDefault={streamingDefault === p.id}
                    keys={streamingKeys[p.id] || {}}
                    onKeyChange={(fk, val) => setStreamingKey(p.id, fk, val)}
                    onSetDefault={() => setStreamingDefault(p.id)}
                    isExpanded={expandedStreaming === p.id}
                    onToggleExpand={() => setExpandedStreaming(prev => prev === p.id ? null : p.id)}
                  />
                ))}
              </div>

              <InfoBanner emoji="🔐" color="amber">
                Stream keys are sensitive. They are stored locally in your browser. Rotate any exposed stream key immediately from your platform's dashboard.
              </InfoBanner>
            </motion.div>
          )}
        </AnimatePresence>

        {!overrideStreaming && (
          <div className="flex items-center gap-3 p-4 bg-brand-light rounded-2xl border border-brand-dark">
            <SafeIcon icon={FiGlobe} className="w-4 h-4 text-text-muted flex-shrink-0" />
            <p className="text-[11px] text-text-muted leading-relaxed">
              Using global default: <strong className="text-accent-navy">{STREAMING_PLATFORMS.find(p => p.id === ((() => { try { return JSON.parse(localStorage.getItem('em_streaming_integrations') || '{}').defaultPlatform; } catch { return 'youtube'; } })()))?.label || 'YouTube Live'}</strong>. Enable the override above to configure a dedicated streaming account for this memorial.
            </p>
          </div>
        )}
      </div>

      {/* Save button */}
      <div className="flex items-center justify-end gap-4 pt-2">
        <AnimatePresence>
          {saved && (
            <motion.div
              initial={{ opacity: 0, x: 10 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0 }}
              className="flex items-center gap-2 text-green-600 text-xs font-black uppercase tracking-widest"
            >
              <SafeIcon icon={FiCheck} className="w-4 h-4" />
              Platform preferences saved
            </motion.div>
          )}
        </AnimatePresence>
        <button
          onClick={handleSave}
          className="flex items-center gap-3 px-8 py-4 bg-accent-navy text-white rounded-2xl text-[11px] font-black uppercase tracking-widest hover:bg-accent-navy/90 transition-all shadow-xl active:scale-[0.98]"
        >
          <SafeIcon icon={FiSave} className="w-4 h-4" />
          Save Platform Preferences
        </button>
      </div>
    </div>
  );
};

export default MemorialPlatformSettings;